<template>
  <div class="agriculture-section">
    <!-- 背景图 -->
    <div class="background-overlay">
      <img :src="backgroundImage" :alt="t('home.scroll.agricultureBackground')" class="background-image" />
    </div>

    <!-- 标题 -->
    <div class="title-container">
      <h1>{{ t('home.scroll.mainTitle') }}</h1>
      <p>{{ t('home.scroll.subtitle') }}</p>
    </div>

    <!-- 横向滚动容器 -->
    <div class="scroll-section" ref="scrollSectionRef">
      <div class="scroll-container">
        <div ref="scrollWrapper" class="scroll-wrapper">
          <!-- 滚动项目 -->
          <div
            v-for="(item, index) in items"
            :key="index"
            class="scroll-item"
            :class="{ active: currentIndex === index }"
          >
            <!-- 交替显示在顶部或底部 -->
            <div
              v-if="index % 2 === 0"
              class="content top-content"
            >
              <span class="number">{{ item.number }}</span>
              <span class="title">{{ t(item.titleKey) }}</span>
            </div>

            <div class="image-box">
              <img :src="item.image" :alt="t(item.titleKey)" class="item-image" />
            </div>

            <div
              v-if="index % 2 === 1"
              class="content bottom-content"
            >
              <span class="number">{{ item.number }}</span>
              <span class="title">{{ t(item.titleKey) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// 使用 i18n
const { locale, t } = useI18n();

// 导入本地图片
import backgroundImage from "../../assets/image/home/scrolbg.jpg";
import itemImage1 from "../../assets/image/home/scrol-2.jpg";
import itemImage2 from "../../assets/image/home/scrol-3.jpg";
import itemImage3 from "../../assets/image/home/scrol-4.jpg";
import itemImage4 from "../../assets/image/home/scorl-5.webp";
import itemImage5 from "../../assets/image/home/scrol-6.jpg";
import itemImage6 from "../../assets/image/home/scrol-1.jpg";

// 注册ScrollTrigger插件
gsap.registerPlugin(ScrollTrigger);

// 定义数据
const items = [
  {
    number: "01",
    titleKey: "home.scroll.items.01",
    image: itemImage1,
  },
  {
    number: "02",
    titleKey: "home.scroll.items.02",
    image: itemImage2,
  },
  {
    number: "03",
    titleKey: "home.scroll.items.03",
    image: itemImage3
  },
   {
    number: "04",
    titleKey: "home.scroll.items.04",
    image: itemImage4
  },
  {
    number: "05",
    titleKey: "home.scroll.items.05",
    image: itemImage5
  },
  {
    number: "06",
    titleKey: "home.scroll.items.06",
    image: itemImage6
  },
];

// 状态管理
const currentIndex = ref(0);
const scrollWrapper = ref(null);
const scrollSectionRef = ref(null);

// GSAP动画控制
let tl = null;

// 初始化GSAP动画时间轴
onMounted(() => {
  // 创建GSAP时间轴
  tl = gsap.timeline({
    scrollTrigger: {
      trigger: scrollSectionRef.value,
      start: "top bottom",
      end: "bottom top",
      scrub: 1,
      onUpdate: (self) => {
        // 计算当前索引
        const newIndex = Math.floor(self.progress * (items.length - 1));
        if (currentIndex.value !== newIndex) {
          currentIndex.value = newIndex;
        }
      },
    },
    
  });

  // 添加动画到时间轴
  tl.fromTo(
    scrollWrapper.value,
    { x: 300 },
    {
      x: -(items.length - 1) * 350,
      duration: 1,
      ease: "none",
    }
  );
});

// 清理资源
onUnmounted(() => {
  if (tl) {
    tl.kill();
  }
  // 清理ScrollTrigger
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
});
</script>

<style scoped>
.agriculture-section {
  position: relative;
  width: 100%;
  padding: 6rem 0;
  box-sizing: border-box;
  overflow: hidden;
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: -1;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.title-container {
  text-align: center;
  color: white;
  padding: 20px;
}

.title-container h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
  font-weight: 500;
}

.title-container p {
  font-size: 1.6rem;
  opacity: 0.9;
}

.scroll-section {
  width: 100%;
}

.scroll-container {
  width: 100%;
  min-height: 500px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
}

.scroll-wrapper {
  display: flex;
  gap: 30px;
  padding: 20px 0;
  width: max-content;
}

.scroll-item {
  flex: 0 0 auto;
  border-radius: 15px;
  position: relative;
  opacity: 1;
  transform: scale(0.9);
  transition: all 0.3s ease;
  position: relative;
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.scroll-item.active {
  opacity: 1;
  transform: scale(1);
}

.image-box {
  aspect-ratio: 408 / 501;
  border: 2px solid hsla(0, 0%, 100%, 0.47);
  border-radius: 2.4rem;
  overflow: hidden;
  position: relative;
  width: 26rem;
  flex-shrink: 0;
}

.item-image {
  height: 100%;
  left: 0;
  max-width: none;
  position: absolute;
  top: 0;
  width: auto;
}

.content {
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  text-align: center;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 26rem;
}

.number {
  font-size: 1.4rem;
  margin-right: 10px;
}

.title {
  font-size: 1.4rem;
  line-height: 1.6;
  word-wrap: break-word;
}

.top-content {
  order: -1;
}

.bottom-content {
  order: 1;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .title-container h1 {
    font-size: 1.6rem;
  }

  .title-container p {
    font-size: 1.2rem;
  }

  .scroll-container {
    width: 100%;
  }
  .image-box{
    width: 25rem;
  }

  .scroll-item {
    width: 25rem;
  }

  .content {
    padding: 10px;
    font-size: 0.9rem;
  }

  .number {
    font-size: 1rem;
  }
}
</style>